import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet } from "../../services/api";
import Button from "../common/Button";

interface BillRow {
  id: number;
  created_at?: string;
  date?: string;
  time?: string;
  patient?: string;
  mobile?: string;
  total?: number;
}

function isoToday(): string {
  return new Date().toISOString().slice(0, 10);
}

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

export default function PharmacyBillHistoryPanel() {
  const [q, setQ] = useState("");
  const [from, setFrom] = useState(() => isoDaysAgo(30));
  const [to, setTo] = useState(() => isoToday());

  const [rows, setRows] = useState<BillRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const limit = 50;

  const queryKey = useMemo(() => `${q}|${from}|${to}`, [q, from, to]);

  const load = useCallback(
    async ({ reset, offset }: { reset: boolean; offset: number }) => {
      try {
        setLoading(true);
        const nextOffset = reset ? 0 : offset;
        const res = await apiGet(
          `/api/pharmacy/bills?q=${encodeURIComponent(q)}&date_from=${encodeURIComponent(from)}&date_to=${encodeURIComponent(to)}&limit=${limit}&offset=${nextOffset}`
        );
        const arr = Array.isArray(res) ? (res as BillRow[]) : [];
        if (reset) {
          setRows(arr);
        } else {
          setRows((prev) => [...prev, ...arr]);
        }
        setHasMore(arr.length === limit);
      } catch (e) {
        console.error("Failed to load pharmacy bill history", e);
        if (reset) {
          setRows([]);
        }
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [q, from, to]
  );

  useEffect(() => {
    load({ reset: true, offset: 0 });
  }, [queryKey, load]);

  const inputStyle = { padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', outline: 'none' };

  return (
    <div className="mod-standalone-window" style={{ display: 'flex', flexDirection: 'column', gap: '20px', minHeight: 'calc(100vh - 120px)' }}>
      
      <div className="mod-window-header" style={{ paddingBottom: 0, borderBottom: 'none', marginBottom: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>🧾 Sales History</h2>
            <p className="mod-text-muted" style={{ margin: '4px 0 0 0' }}>Review past pharmacy transactions and reprint bills.</p>
          </div>
          
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="search"
              placeholder="Search patient or mobile..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ ...inputStyle, minWidth: '220px' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#f1f5f9', padding: '4px', borderRadius: '10px' }}>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={inputStyle} />
              <span style={{ color: '#64748b', fontWeight: 600 }}>to</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={inputStyle} />
            </div>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px' }}>
        {loading && rows.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading history...</div>
        ) : rows.length === 0 ? (
          <div className="mod-empty-state" style={{ height: '300px', border: '1px dashed #cbd5e1' }}>
            No sales found for this period.
          </div>
        ) : (
          rows.map((r) => (
            <div key={r.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: 'var(--mod-shadow-sm)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <strong style={{ fontSize: '1.05rem', color: '#0f172a' }}>{r.patient || "Walk-in Patient"}</strong>
                  <span style={{ fontSize: '0.75rem', background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: '6px', fontWeight: 600 }}>
                    {r.date || (r.created_at ? r.created_at.slice(0, 10) : "—")} • {r.time || "—"}
                  </span>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#64748b' }}>📞 {r.mobile || "No Contact"}</div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#16a34a' }}>
                  ₹{Number(r.total || 0).toLocaleString("en-IN")}
                </div>
                <Button variant="outline" size="sm" onClick={() => window.open(`/print?type=pharmacy&bill=${r.id}`, "_blank")}>
                  🖨️ Print
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <div style={{ flexShrink: 0, padding: '16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '0 0 16px 16px' }}>
        <div style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>Showing {rows.length} records</div>
        <Button variant="secondary" onClick={() => load({ reset: false, offset: rows.length })} disabled={loading || !hasMore} isLoading={loading}>
          {hasMore ? "Load More Records" : "End of History"}
        </Button>
      </div>
    </div>
  );
}
